import { GEOAPIFY_AUTOCOMPLETE_URL, SEARCH_EUROPE_BBOX } from "../constants";

import SearchEngine from "./SearchEngine";
import { SEARCH_RESULT_TYPE_PRIORITY, SearchResultType } from "./types";

import type {
  GeoJSONGeometry,
  SearchEngineSearchOptions,
  SearchResult,
} from "./types";

/** Geoapify `result_type` values considered as a place / municipality. */
const PLACE_RESULT_TYPES = [
  "country",
  "state",
  "county",
  "city",
  "postcode",
  "district",
  "suburb",
];

/** Geoapify `result_type` values considered as an address. */
const ADDRESS_RESULT_TYPES = ["street", "building"];

/**
 * Geoapify `result_type` values whose full geometry (a polygon for places, a
 * line for streets) can be fetched via the Place Details API. Other types
 * (buildings, pois) only ever resolve to point geometries, so fetching their
 * details brings no benefit.
 */
const DETAILED_GEOMETRY_RESULT_TYPES = [...PLACE_RESULT_TYPES, "street"];

interface GeoapifyProperties {
  address_line1?: string;
  address_line2?: string;
  bbox?: { lat1: number; lat2: number; lon1: number; lon2: number };
  categories?: string[];
  city?: string;
  country?: string;
  formatted?: string;
  name?: string;
  place_id?: string;
  result_type?: string;
  state?: string;
}

export type PlaceFeature = {
  bbox?: [number, number, number, number];
  geometry: GeoJSONGeometry;
  properties: GeoapifyProperties;
  type: "Feature";
};

export interface PlacesSearchEngineOptions {
  apiKey: string;
  bbox?: [number, number, number, number];
  limit?: number;
  priority?: number;
  url?: string;
}

const getResultType = (properties: GeoapifyProperties): SearchResultType => {
  const resultType = properties.result_type;

  if (PLACE_RESULT_TYPES.includes(`${resultType}`)) {
    return SearchResultType.PLACE;
  }
  if (ADDRESS_RESULT_TYPES.includes(`${resultType}`)) {
    return SearchResultType.ADDRESS;
  }
  return SearchResultType.POI;
};

/**
 * Returns the Geoapify `place_id` of the result if its actual geometry can be
 * fetched via the Place Details API, undefined otherwise (eg. point data such
 * as pois or building addresses).
 */
export const getDetailedGeometryPlaceId = (
  result: SearchResult<PlaceFeature>,
): string | undefined => {
  const properties = result.feature?.properties;
  if (
    !properties?.place_id ||
    !DETAILED_GEOMETRY_RESULT_TYPES.includes(`${properties.result_type}`)
  ) {
    return undefined;
  }
  return properties.place_id;
};

// Geoapify returns the bounding box either on the feature or in its properties.
const getBbox = (
  feature: PlaceFeature,
): [number, number, number, number] | undefined => {
  if (feature.bbox?.length === 4) {
    return feature.bbox as [number, number, number, number];
  }
  const bbox = feature.properties?.bbox;
  if (bbox) {
    return [bbox.lon1, bbox.lat1, bbox.lon2, bbox.lat2];
  }
  return undefined;
};

/**
 * Search engine querying the Geoapify geocoding autocomplete api for places,
 * addresses and pois. Results are restricted to Europe.
 */
class PlacesSearchEngine extends SearchEngine<PlaceFeature> {
  readonly id = "places";

  private readonly apiKey: string;

  private readonly bbox: [number, number, number, number];

  private readonly limit: number;

  private readonly url: string;

  constructor({
    apiKey,
    bbox = SEARCH_EUROPE_BBOX,
    limit = 10,
    priority = 1,
    url = GEOAPIFY_AUTOCOMPLETE_URL,
  }: PlacesSearchEngineOptions) {
    super({ priority });
    this.apiKey = apiKey;
    this.bbox = bbox;
    this.limit = limit;
    this.url = url;
  }

  canSearch(query: string): boolean {
    return !!this.apiKey && super.canSearch(query);
  }

  protected async doSearch(
    query: string,
    { lang, signal }: SearchEngineSearchOptions,
  ): Promise<SearchResult<PlaceFeature>[]> {
    const [minLon, minLat, maxLon, maxLat] = this.bbox;
    const searchParams = new URLSearchParams({
      apiKey: this.apiKey,
      filter: `rect:${minLon},${minLat},${maxLon},${maxLat}`,
      format: "geojson",
      limit: `${this.limit}`,
      text: query,
    });
    if (lang) {
      searchParams.set("lang", lang);
    }
    const response = await fetch(`${this.url}?${searchParams.toString()}`, {
      signal,
    });
    const data = await response.json();
    if (data.error || data.message) {
      throw new Error(data.error || data.message);
    }
    return ((data.features || []) as PlaceFeature[])
      .map((feature, index) => {
        const properties = feature.properties || {};
        const type = getResultType(properties);
        return {
          bbox: getBbox(feature),
          feature,
          geometry: feature.geometry,
          id: `place-${properties.place_id ?? index}`,
          label:
            properties.name ||
            properties.address_line1 ||
            properties.formatted ||
            "",
          rank: index,
          subtitle: properties.address_line2 || properties.formatted,
          type,
        };
      })
      .filter((result) => {
        return !!result.label && !!result.geometry;
      })
      .sort((a, b) => {
        return (
          SEARCH_RESULT_TYPE_PRIORITY[a.type] -
            SEARCH_RESULT_TYPE_PRIORITY[b.type] || a.rank - b.rank
        );
      });
  }
}

export default PlacesSearchEngine;
